import React, { ReactNode, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ReactComponent as Logo } from "@/assets/dinamo-logo.svg";
import { ReactComponent as LogoutIcon } from "@/assets/icons/logout.svg";
import { Button, Tabs, Burger, Menu, Group } from "@mantine/core";
import { useMe } from "@/context/MeContext";
import "./Layout.css";

interface AdminLayoutProps {
  children: ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { me, logout } = useMe();

  const currentRoute = useLocation().pathname;
  const pages = ["analytics", "magazines", "publications", "users"];
  const [activeTab, setActiveTab] = useState<string>("analytics");
  const [opened, setOpened] = useState(false);
  const toggleMenu = () => setOpened((prev) => !prev);

  const goToPage = (value: string | null) => {
    if (value) {
      setActiveTab(value);
      navigate(`/admin/${value}`);
    }
  };

  const navigateFromMenu = (value: string) => {
    setActiveTab(value);
    navigate(`/admin/${value}`);
  };

  const goHome = () => {
    navigate("/admin/analytics");
  };

  const logoutHandler = async () => {
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    if (!me || me.type !== "ADMIN") {
      navigate("/");
    }
    if (!currentRoute.includes(activeTab))
      setActiveTab(pages.find((page) => currentRoute.includes(page)) || "");
  });

  return (
    <>
      <nav className="admin header">
        <div
          className="flex gap-3 items-center cursor-pointer"
          onClick={goHome}
        >
          <Logo />
          <div className="text-[#004a99] text-base font-bold">
            Digitalne revije
          </div>
        </div>
        <div className="flex items-end">
          <Tabs value={activeTab} onChange={goToPage} className="items-center">
            <Tabs.List justify="center">
              <Tabs.Tab
                value="analytics"
                className="!py-[1.25rem] !px-0 mx-4 hover:bg-[transparent]"
              >
                <span
                  className={
                    activeTab === "analytics" ? "text-[#004a99] font-bold" : ""
                  }
                >
                  Analitika
                </span>
              </Tabs.Tab>
              <Tabs.Tab
                value="magazines"
                className="!py-[1.25rem] !px-0 mx-4 hover:bg-[transparent]"
              >
                <span
                  className={
                    activeTab === "magazines" ? "text-[#004a99] font-bold" : ""
                  }
                >
                  Revije
                </span>
              </Tabs.Tab>
              <Tabs.Tab
                value="publications"
                className="!py-[1.25rem] !px-0 mx-4 hover:bg-[transparent]"
              >
                <span
                  className={
                    activeTab === "publications"
                      ? "text-[#004a99] font-bold"
                      : ""
                  }
                >
                  Publikacije
                </span>
              </Tabs.Tab>
              <Tabs.Tab
                value="users"
                className="!py-[1.25rem] !px-0 mx-4 hover:bg-[transparent]"
              >
                <span
                  className={
                    activeTab === "users" ? "text-[#004a99] font-bold" : ""
                  }
                >
                  Korisnici
                </span>
              </Tabs.Tab>
            </Tabs.List>
          </Tabs>
        </div>
        <div className="flex items-center">
          <Button
            leftSection={<LogoutIcon />}
            variant="transparent"
            color="#525252"
            className="text-button"
            onClick={logoutHandler}
          >
            Odjava
          </Button>
        </div>
      </nav>
      <nav className="admin-mobile-menu-header">
        <div className="relative">
          <Menu
            shadow="lg"
            opened={opened}
            onClose={() => setOpened(false)}
            position="left-start"
            classNames={{
              dropdown: "menu-dropdown",
              item: "menu-item",
            }}
          >
            <Menu.Target>
              <Burger opened={opened} onClick={toggleMenu} size="sm" />
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => navigateFromMenu("analytics")}>
                <span
                  className={
                    activeTab === "analytics" ? "text-[#004a99] font-bold" : ""
                  }
                >
                  Analitika
                </span>
              </Menu.Item>
              <Menu.Item onClick={() => navigateFromMenu("magazines")}>
                <span
                  className={
                    activeTab === "magazines" ? "text-[#004a99] font-bold" : ""
                  }
                >
                  Revije
                </span>
              </Menu.Item>
              <Menu.Item onClick={() => navigateFromMenu("publications")}>
                <span
                  className={
                    activeTab === "publications"
                      ? "text-[#004a99] font-bold"
                      : ""
                  }
                >
                  Publikacije
                </span>
              </Menu.Item>
              <Menu.Item onClick={() => navigateFromMenu("users")}>
                <span
                  className={
                    activeTab === "users" ? "text-[#004a99] font-bold" : ""
                  }
                >
                  Korisnici
                </span>
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item onClick={logout}>
                <Group gap={10}>
                  <LogoutIcon />
                  <span className="text-[#525252] font-medium">Odjava</span>
                </Group>
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </div>
      </nav>
      <div className="admin-content scroll-container">{children}</div>
    </>
  );
};

export default AdminLayout;
